<script setup lang="ts">
import {ref, watch} from 'vue'
import {Loader2, Pencil, Trash2, X, Check} from 'lucide-vue-next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {ScrollArea} from '@/components/ui/scroll-area'
import {useToast} from '@/composables/useToast'
import {useAppStore} from '@/stores/app'
import type {Memory} from '@backend/types'

const open = defineModel<boolean>('open', {required: true})

const appStore = useAppStore()
const toast = useToast()

const loading = ref(false)
const memories = ref<Memory[]>([])
const editingId = ref<number | null>(null)
const editContent = ref('')
const savingId = ref<number | null>(null)
const deleteTarget = ref<Memory | null>(null)
const deletingId = ref<number | null>(null)

watch(open, async (isOpen) => {
  if (!isOpen || !appStore.apiService) return

  loading.value = true
  editingId.value = null
  try {
    memories.value = await appStore.apiService.getMemories()
  } catch {
    toast.error('Не удалось загрузить память')
  } finally {
    loading.value = false
  }
})

function startEdit(memory: Memory) {
  editingId.value = memory.memory_id
  editContent.value = memory.content
}

function cancelEdit() {
  editingId.value = null
  editContent.value = ''
}

async function saveEdit(memory: Memory) {
  if (!appStore.apiService || !editContent.value.trim()) return

  savingId.value = memory.memory_id
  try {
    const updated = await appStore.apiService.updateMemory(memory.memory_id, {
      content: editContent.value.trim(),
    })
    const idx = memories.value.findIndex((m) => m.memory_id === memory.memory_id)
    if (idx !== -1) memories.value[idx] = updated
    editingId.value = null
    toast.success('Память обновлена')
  } catch {
    toast.error('Не удалось обновить')
  } finally {
    savingId.value = null
  }
}

async function confirmDelete() {
  if (!appStore.apiService || !deleteTarget.value) return

  const id = deleteTarget.value.memory_id
  deletingId.value = id
  try {
    await appStore.apiService.deleteMemory(id)
    memories.value = memories.value.filter((m) => m.memory_id !== id)
    toast.success('Память удалена')
  } catch {
    toast.error('Не удалось удалить')
  } finally {
    deletingId.value = null
    deleteTarget.value = null
  }
}

function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Память Дживса</DialogTitle>
        <DialogDescription>Факты и заметки, которые Дживс помнит о вас</DialogDescription>
      </DialogHeader>

      <div v-if="loading" class="flex items-center justify-center py-8">
        <Loader2 class="size-6 animate-spin text-muted-foreground" />
      </div>

      <div v-else-if="memories.length === 0" class="py-8 text-center text-sm text-muted-foreground">
        Пока нет сохранённых воспоминаний. Расскажите Дживсу что-нибудь о себе в Telegram-чате.
      </div>

      <ScrollArea v-else class="max-h-[60vh]">
        <div class="space-y-3 pr-3">
          <div v-for="memory in memories" :key="memory.memory_id" class="rounded-lg border p-3 text-sm">
            <template v-if="editingId === memory.memory_id">
              <Textarea v-model="editContent" class="mb-2 min-h-[80px]" @keydown.ctrl.enter="saveEdit(memory)" />
              <div class="flex justify-end gap-2">
                <Button variant="ghost" size="sm" @click="cancelEdit" :disabled="savingId === memory.memory_id">
                  <X class="mr-1 size-3.5" />
                  Отмена
                </Button>
                <Button size="sm" @click="saveEdit(memory)" :disabled="savingId === memory.memory_id">
                  <Loader2 v-if="savingId === memory.memory_id" class="mr-1 size-3.5 animate-spin" />
                  <Check v-else class="mr-1 size-3.5" />
                  Сохранить
                </Button>
              </div>
            </template>

            <template v-else>
              <div class="mb-1.5 whitespace-pre-wrap">{{ memory.content }}</div>
              <div class="flex items-center justify-between">
                <span class="text-xs text-muted-foreground">
                  {{ formatDate(memory.created_at) }}
                  <template v-if="memory.expired_at"> &middot; истекает {{ formatDate(memory.expired_at) }} </template>
                </span>
                <div class="flex gap-1">
                  <Button variant="ghost" size="icon" class="size-7" @click="startEdit(memory)">
                    <Pencil class="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="size-7 text-destructive hover:text-destructive"
                    @click="deleteTarget = memory">
                    <Trash2 class="size-3.5" />
                  </Button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>

  <AlertDialog
    :open="!!deleteTarget"
    @update:open="
      (v) => {
        if (!v) deleteTarget = null
      }
    ">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Удалить память?</AlertDialogTitle>
        <AlertDialogDescription> Дживс забудет этот факт. Это действие нельзя отменить. </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel :disabled="!!deletingId">Отмена</AlertDialogCancel>
        <Button variant="destructive" :disabled="!!deletingId" @click="confirmDelete">
          <Loader2 v-if="deletingId" class="mr-2 size-4 animate-spin" />
          Удалить
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
